import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HiStar, HiX, HiOutlineChevronLeft, HiOutlineUser, HiOutlineClock, HiOutlineCalendar } from 'react-icons/hi';
import { tmdbAPI } from '../services/api';

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w500';
const TMDB_BACKDROP_BASE = 'https://image.tmdb.org/t/p/w1280';

function MediaModal({ item, onClose }) {
  const { t } = useTranslation();
  const [details, setDetails] = useState(null);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);

  const mediaType = item.media_type || (item.first_air_date ? 'tv' : 'movie');
  const title = item.title || item.name;
  const releaseDate = item.release_date || item.first_air_date;
  const year = releaseDate ? new Date(releaseDate).getFullYear() : '';

  useEffect(() => {
    loadDetails();
    loadCredits();
  }, [item.id, mediaType]);

  const loadDetails = async () => {
    try {
      const response = mediaType === 'movie' 
        ? await tmdbAPI.getMovieDetails(item.id)
        : await tmdbAPI.getTVDetails(item.id);
      setDetails(response.data);
    } catch (error) {
      console.error('Error loading details:', error);
    }
  };

  const loadCredits = async () => {
    try {
      const response = mediaType === 'movie'
        ? await tmdbAPI.getMovieCredits(item.id)
        : await tmdbAPI.getTVCredits(item.id);
      setCredits(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading credits:', error);
      setLoading(false);
    }
  };

  const backdropPath = details?.backdrop_path || item.backdrop_path;
  const genres = details?.genres || [];
  const runtime = details?.runtime || (details?.episode_run_time?.[0]);
  const cast = credits?.cast?.slice(0, 10) || [];
  const crew = credits?.crew?.slice(0, 5) || [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal media-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <button className="modal-close mobile-back" onClick={onClose}>
            <HiOutlineChevronLeft />
          </button>
          <h2>{title}</h2>
          <button className="modal-close desktop-close" onClick={onClose}>
            <HiX />
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
            </div>
          ) : (
            <>
              {backdropPath && (
                <div className="media-modal-backdrop">
                  <img src={`${TMDB_BACKDROP_BASE}${backdropPath}`} alt={title} />
                </div>
              )}

              <div className="media-modal-content">
                <div className="media-modal-right">
                  <div className="media-modal-meta">
                    <span className="media-modal-badge">
                      {mediaType === 'movie' ? t('navigation.movies').slice(0, -1) : t('navigation.series').slice(0, -1)}
                    </span>
                    {year && <span>{year}</span>}
                    {runtime && (
                      <span className="media-modal-runtime">
                        <HiOutlineClock />
                        {runtime} {t('media.min')}
                      </span>
                    )}
                    {details?.vote_average > 0 && (
                      <span className={`media-modal-rating ${
                        details.vote_average <= 6.0 ? 'rating-low' :
                        details.vote_average <= 8.0 ? 'rating-medium' :
                        'rating-high'
                      }`}>
                        <HiStar />
                        {details.vote_average.toFixed(1)}
                      </span>
                    )}
                  </div>

                  {genres.length > 0 && (
                    <div className="media-modal-genres">
                      {genres.map((genre) => (
                        <span key={genre.id} className="genre-tag">
                          {genre.name}
                        </span>
                      ))}
                    </div>
                  )}

                  {details?.overview && (
                    <div className="media-modal-overview">
                      <h3>{t('media.synopsis')}</h3>
                      <p>{details.overview}</p>
                    </div>
                  )}

                  {mediaType === 'tv' && details?.number_of_seasons > 0 && (
                    <div className="media-modal-seasons">
                      <h3>
                        <HiOutlineCalendar />
                        {details.number_of_seasons} {details.number_of_seasons !== 1 ? t('series.seasons') : t('series.season')}
                      </h3>
                    </div>
                  )}

                  {cast.length > 0 && (
                    <div className="media-modal-cast">
                      <h3>{t('media.mainCast')}</h3>
                      <div className="cast-list">
                        {cast.map((actor) => (
                          <div key={actor.id} className="cast-item">
                            {actor.profile_path ? (
                              <img
                                src={`${TMDB_IMAGE_BASE}${actor.profile_path}`}
                                alt={actor.name}
                              />
                            ) : (
                              <div className="no-avatar">
                                <HiOutlineUser />
                              </div>
                            )}
                            <div className="cast-info">
                              <span className="actor-name">{actor.name}</span>
                              {actor.character && (
                                <span className="character-name">{actor.character}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {crew.length > 0 && (
                    <div className="media-modal-crew">
                      <h3>{t('media.team')}</h3>
                      <div className="crew-list">
                        {crew.map((member) => (
                          <div key={member.id} className="crew-item">
                            <span className="crew-name">{member.name}</span>
                            <span className="crew-role">{member.job}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MediaModal;
